import { Geotab, User } from "@fairfleet/geotab";
import { QueryKey, useQuery } from "@tanstack/react-query";
import { uniq } from "lodash";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_USER } from "@/constants/resultsLimits";
import { chunkArray } from "@/utilities/chunkArray";

/**
 * Loads users by id with `UserSearch.userIds`, one `Get` per {@link RESULTS_LIMIT_USER} ids.
 * Deliberately not group-filtered: a referenced user outside the selected groups is still named.
 * Throws when a page holds a user that was not asked for: then the server ignored `userIds`, and
 * the result would name drivers wrongly instead of failing visibly.
 */
export async function fetchUsersByUserIds(
  geotabClient: Geotab,
  ids: string[],
  signal?: AbortSignal,
): Promise<User[]> {
  const requested = new Set(ids);
  const pages = await Promise.all(
    chunkArray(ids, RESULTS_LIMIT_USER).map(userIds =>
      geotabClient.get("User", { userIds }, RESULTS_LIMIT_USER, signal),
    ),
  );
  const users = pages.flat();
  if (users.some(u => !u.id || !requested.has(u.id))) {
    throw new Error("Get User returned users that were not requested; UserSearch.userIds was ignored.");
  }
  return users;
}

/**
 * `placeholderData` that keeps the previous users while the id list changes, but not across a
 * session switch: user ids such as `b1` repeat across databases and would name the wrong driver.
 */
export function previousDataOfSession(activeSessionId: string | null | undefined) {
  return (previous: User[] | undefined, previousQuery: { queryKey: QueryKey } | undefined) =>
    previousQuery?.queryKey[1] === activeSessionId ? previous : undefined;
}

/**
 * The users with the given ids, regardless of the group filter: one request per
 * {@link RESULTS_LIMIT_USER} ids. `undefined` (ids not known yet) disables the query; an empty
 * list resolves to no users without a request. The previous result stays shown while a changed,
 * non-empty id list loads (`isPlaceholderData`).
 */
export function useUsersByUserIds(ids: string[] | undefined) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const uniqueIds = ids ? uniq(ids).sort() : [];
  return useQuery<User[]>({
    queryKey: ["usersByUserIds", activeSessionId, uniqueIds],
    queryFn: ({ signal }) => fetchUsersByUserIds(geotabClient, uniqueIds, signal),
    enabled: ids !== undefined,
    placeholderData: uniqueIds.length > 0 ? previousDataOfSession(activeSessionId) : undefined,
  });
}
